import React, { useState } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Italic from '@tiptap/extension-italic';

import {
  InputBottom,
  InputContainer,
  InputIcon,
  Separator,
  SeparatorInvisible,
  OpenLinkPrompt,
  LinkPromptWrapper,
  LinkPromptHeading,
  LinkPromptActionButton,
  LinkPromptActions,
  Disabled,
} from './HistoryTextFormat.styles';

interface IHistoryTextFormat {
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  onMutateMessage: () => void;
  disabled?: boolean;
}

export const HistoryTextFormat = ({
  text,
  setText,
  onMutateMessage,
  disabled,
}: IHistoryTextFormat) => {
  const [openLinkPrompt, setOpenLinkPrompt] = useState(false);
  const [promptLink, setPromptLink] = useState('');

  const textEditor = useEditor({
    autofocus: false,
    extensions: [
      StarterKit,
      Underline,
      Italic,
      Link.configure({
        autolink: false,
        validate: href => /^https?:\/\//.test(href),
      }),
      Placeholder.configure({
        placeholder: 'Click to add a comment',
      }),
    ],
    onUpdate({ editor }) {
      setText(editor && editor.getHTML());
    },
  });

  const setLink = (input: string) => {
    textEditor &&
      textEditor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: input })
        .run();
  };

  if (!textEditor) {
    return null;
  }

  const mutate = () => {
    onMutateMessage();
    setText('');
    textEditor.commands.clearContent();
  };

  return (
    <InputContainer>
      {disabled && <Disabled />}
      <EditorContent
        editor={textEditor}
        className="editableContent"
        value={text}
        onFocus={() => setOpenLinkPrompt(false)}
        onKeyDown={e => {
          if (e.keyCode === 13 && !e.shiftKey) {
            mutate();
          }
        }}
      />
      <InputBottom>
        <InputIcon
          onClick={() => textEditor.chain().focus().toggleBold().run()}
          disabled={!textEditor.can().chain().focus().toggleBold().run()}
          data-istextformatcommandactive={textEditor.isActive('bold')}
        >
          <BlueIcon icon="bold" color="#8F95B2" />
        </InputIcon>
        <InputIcon
          onClick={() => textEditor.chain().focus().toggleItalic().run()}
          disabled={!textEditor.can().chain().focus().toggleItalic().run()}
          data-istextformatcommandactive={textEditor.isActive('italic')}
        >
          <BlueIcon icon="italic" color="#8F95B2" />
        </InputIcon>
        <InputIcon
          onClick={() => textEditor.chain().focus().toggleUnderline().run()}
          disabled={!textEditor.can().chain().focus().toggleUnderline().run()}
          data-istextformatcommandactive={textEditor.isActive('underline')}
        >
          <BlueIcon icon="underline" color="#8F95B2" />
        </InputIcon>
        <Separator />

        <InputIcon
          onClick={() => setOpenLinkPrompt(true)}
          data-istextformatcommandactive={textEditor.isActive('link')}
        >
          <BlueIcon icon="link" color="#8F95B2" />
        </InputIcon>
        <Separator />
        <InputIcon
          onClick={() => textEditor.chain().focus().toggleBulletList().run()}
          data-istextformatcommandactive={textEditor.isActive('bulletList')}
        >
          <BlueIcon icon="list" color="#8F95B2" />
        </InputIcon>
        <InputIcon
          onClick={() => textEditor.chain().focus().toggleOrderedList().run()}
          data-istextformatcommandactive={textEditor.isActive('orderedList')}
        >
          <BlueIcon icon="numbered-list" color="#8F95B2" />
        </InputIcon>
        <SeparatorInvisible />
        <InputIcon onClick={mutate}>
          <BlueIcon icon="send-message" color="#8F95B2" />
        </InputIcon>
      </InputBottom>

      {openLinkPrompt && (
        <LinkPromptWrapper>
          <LinkPromptHeading>Insert a link</LinkPromptHeading>
          <OpenLinkPrompt
            type="text"
            value={promptLink}
            onChange={e => setPromptLink(e.target.value)}
          />
          <LinkPromptActions>
            <LinkPromptActionButton
              type="button"
              onClick={e => {
                e.preventDefault();
                setLink(promptLink);
                setOpenLinkPrompt(false);
                setPromptLink('');
              }}
              disabled={promptLink.length < 1}
            >
              Ok
            </LinkPromptActionButton>

            <LinkPromptActionButton
              type="button"
              onClick={() => {
                setOpenLinkPrompt(false);
                setPromptLink('');
              }}
            >
              Cancel
            </LinkPromptActionButton>
          </LinkPromptActions>
        </LinkPromptWrapper>
      )}
    </InputContainer>
  );
};
